import Battery2BarIcon from '@mui/icons-material/Battery2Bar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import ToysIcon from '@mui/icons-material/Toys';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { addDays } from 'date-fns';
import deLocale from 'date-fns/locale/de';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AuthModule from '../../../auth/AuthModule';
import { useCustomerContractTypeSettings } from '../../../services/customerContractTypeSettings';
import { useSubEquipments } from '../../../services/equipmentData';
import {
  Equipment,
  EquipmentIsChecked,
} from '../../../services/types/subEquipmentResponse';
import { jhTheme } from '../../../themeProvider';
import { InternalContractDetail } from '../../BaseController/types/internalContract';
import { CustomButton } from '../../common/button/customButton';
import { Utils } from '../../common/utils/utils';

export interface EquipmentListProps {
  serialNumber: any;
  currentEquipment: InternalContractDetail;
  contractNumber: string;
  contractPosition: string;
  setShowBusy: (busy: boolean) => void;
}

export const EquipmentList = (props: EquipmentListProps) => {
  const {
    serialNumber,
    currentEquipment,
    contractNumber,
    contractPosition,
    setShowBusy,
  } = props;

  const [allEquipments, setAllEquipments] = useState<Equipment[]>([]);
  const [returnReason, setReturnReason] = useState<string>('');
  const [requestedChangeDate, setRequestedChangeDate] = useState<Date>(
    new Date(),
  );
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [checkedEquipments, setIsChecked] = useState<EquipmentIsChecked[]>([]);
  const { subEquipments, isLoading } = useSubEquipments(serialNumber);

  const navigate = useNavigate();

  const { beginDate } = currentEquipment;
  const { endDate } = currentEquipment;

  const {
    CustomerContractTypeSettingsData,
    isLoading: isLoadingCustomerContractTypeSettingsData,
  } = useCustomerContractTypeSettings(
    String(currentEquipment.customerNumber),
    String(currentEquipment.contractUsageName),
  );

  const checkDateIsAllowed = (date: Date) => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - 1);

    if (
      date < Utils.parseDateStringToDate(endDate) &&
      date > Utils.parseDateStringToDate(beginDate) &&
      date > currentDate
    ) {
      return true;
    }
    return false;
  };

  const checkEquipmentIsChecked = () => {
    const equipmentIsChecked = checkedEquipments.some((e) => {
      if (e.isChecked === true) {
        return true;
      }
      return false;
    });
    return equipmentIsChecked;
  };

  const checkMandatory = () => {
    if (checkEquipmentIsChecked() && checkDateIsAllowed(requestedChangeDate)) {
      setButtonDisabled(false);
      return true;
    }
    setButtonDisabled(true);
    return false;
  };

  useEffect(() => {
    const currentEquipmentTmp: Equipment = {
      equipmentId: currentEquipment.equipmentSerialNr,
      equipmentDescription: currentEquipment.equipmentName,
      equipmentCategory: 'FN',
    };
    if (subEquipments && subEquipments.length > 0) {
      setAllEquipments([currentEquipmentTmp, ...subEquipments]);
    } else {
      setAllEquipments([currentEquipmentTmp]);
    }
  }, [
    currentEquipment.equipmentName,
    currentEquipment.equipmentSerialNr,
    subEquipments,
  ]);

  useEffect(() => {
    const array: EquipmentIsChecked[] = [];
    allEquipments.forEach((e) => {
      const checked: EquipmentIsChecked = {
        equipmentId: e.equipmentId,
        equipmentCategory: e.equipmentCategory,
        equipmentDescription: e.equipmentDescription,
        isChecked: false,
      };
      array.push(checked);
    });
    setIsChecked(array);
  }, [allEquipments]);

  useEffect(() => {
    checkMandatory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedEquipments, requestedChangeDate]);

  useEffect(() => {
    if (CustomerContractTypeSettingsData) {
      setRequestedChangeDate(
        addDays(new Date(), CustomerContractTypeSettingsData.cancelPeriodDays),
      );
    }
  }, [CustomerContractTypeSettingsData]);

  const handleToggle = (equipmentId: string) => () => {
    let checkedEquipmentsTmp: EquipmentIsChecked[] = [];
    const checkAll = checkedEquipments.some((equipment) => {
      if (
        equipment.equipmentId === currentEquipment.equipmentSerialNr &&
        !equipment.isChecked === true
      ) {
        return true;
      }
      return false;
    });
    if (equipmentId === currentEquipment.equipmentSerialNr) {
      checkedEquipmentsTmp = checkedEquipments.map((equipment) => {
        return { ...equipment, isChecked: checkAll };
      });
      setIsChecked(checkedEquipmentsTmp);
    } else if (checkAll) {
      checkedEquipmentsTmp = checkedEquipments.map((equipment) => {
        if (equipment.equipmentId === equipmentId) {
          return { ...equipment, isChecked: !equipment.isChecked };
        }
        return equipment;
      });
      setIsChecked(checkedEquipmentsTmp);
    }
  };

  if (isLoadingCustomerContractTypeSettingsData) {
    return null;
  }

  const sendData = async () => {
    if (checkMandatory() === true) {
      const equipmentsToRemoveTmp: string[] = [];
      checkedEquipments.forEach((e) => {
        if (e.isChecked) equipmentsToRemoveTmp.push(e.equipmentId);
      });
      setShowBusy(true);
      try {
        const b2cToken =
          await AuthModule.getInstance().getProfileTokenRedirect();
        const resp = await axios({
          method: 'POST',
          url: `${process.env.REACT_APP_BACKEND_API_URL}/contractchangerequests/equipment-removal`,
          data: {
            contractNumber,
            contractPosition,
            requestedChangeDate,
            customerNote: returnReason,
            equipmentsToRemove: equipmentsToRemoveTmp,
          },
          headers: {
            Authorization: `Bearer ${b2cToken}`,
            'Ocp-Apim-Subscription-Key': `${process.env.REACT_APP_API_KEY}`,
          },
        });
        if (resp?.status === 201) {
          setShowBusy(false);
          navigate(`/detail/${contractNumber}/${contractPosition}/X`);
        }
      } catch (error) {
        setShowBusy(false);
      }
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <Card
      sx={{
        width: '100%',
        bgcolor: 'rgb(224,228,232)',
      }}
    >
      <CardContent>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={deLocale}
        >
          <Stack
            direction="row"
            spacing={2}
            alignContent="center"
            justifyContent="space-around"
          >
            <Stack sx={{ width: '100%' }} spacing={2}>
              <Stack
                direction="column"
                alignItems="flex-start"
                sx={{ width: '100%' }}
              >
                <Stack sx={{ width: '100%' }}>
                  <Typography variant="buttonActionHeadline">
                    {t('ButtonContentReturnEquipment')}
                  </Typography>
                  <Divider light />
                </Stack>
              </Stack>
              <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
                {checkedEquipments.map((equipment) => {
                  return (
                    <Card sx={{ width: '100%' }} variant="elevation">
                      <CardActionArea
                        onClick={handleToggle(equipment.equipmentId)}
                      >
                        <CardHeader
                          avatar={
                            <Avatar>
                              {equipment.equipmentCategory === 'B' && (
                                <Battery2BarIcon />
                              )}
                              {equipment.equipmentCategory === 'L' && (
                                <ElectricalServicesIcon />
                              )}
                              {equipment.equipmentCategory !== 'B' &&
                                equipment.equipmentCategory !== 'L' && (
                                  <ToysIcon />
                                )}
                            </Avatar>
                          }
                          title={equipment.equipmentDescription}
                          subheader={equipment.equipmentId}
                          action={
                            <Checkbox
                              sx={{ '& .MuiSvgIcon-root': { fontSize: 32 } }}
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                              id={equipment.equipmentId}
                              checked={equipment.isChecked}
                            />
                          }
                        />
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Stack>

              <Stack direction="row" spacing={2}>
                <Box>
                  <Typography>{t('ReturnDate')}:</Typography>
                  <Stack>
                    <DatePicker
                      disablePast
                      minDate={
                        CustomerContractTypeSettingsData
                          ? addDays(
                              new Date(),
                              CustomerContractTypeSettingsData.cancelPeriodDays,
                            )
                          : new Date()
                      }
                      maxDate={Utils.parseDateStringToDate(endDate)}
                      slotProps={{
                        textField: {
                          className: 'TextField-without-border-radius',
                          sx: {
                            backgroundColor: jhTheme.palette.background.paper,
                          },
                        },
                      }}
                      value={requestedChangeDate}
                      onChange={(newValue) => {
                        if (newValue) {
                          setRequestedChangeDate(newValue);
                        }
                      }}
                      format={t('DateFormatShort') || ''}
                    />
                  </Stack>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography>{t('ReturnReason')}:</Typography>
                  <Stack>
                    <TextField
                      className="TextField-without-border-radius"
                      sx={{
                        width: '100%',
                        backgroundColor: jhTheme.palette.background.paper,
                      }}
                      value={returnReason}
                      onChange={(e) => {
                        setReturnReason(e.target.value);
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
              <Stack>
                {!isLoadingCustomerContractTypeSettingsData &&
                CustomerContractTypeSettingsData ? (
                  <Stack>
                    <Stack direction="row">
                      {CustomerContractTypeSettingsData.cancelPeriodDays ? (
                        <Stack direction="row" spacing={2}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 'bold' }}
                          >
                            {t('DaysNoticePeriod')}:
                          </Typography>
                          <Typography variant="caption">
                            {CustomerContractTypeSettingsData.cancelPeriodDays}{' '}
                            {t('Days')}
                          </Typography>
                        </Stack>
                      ) : null}
                    </Stack>
                    <Stack direction="row">
                      {CustomerContractTypeSettingsData.minDays ? (
                        <Stack direction="row" spacing={2}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 'bold' }}
                          >
                            {t('MinimumTerm')}:
                          </Typography>
                          <Typography variant="caption">
                            {CustomerContractTypeSettingsData.minDays}{' '}
                            {t('Days')}
                          </Typography>
                        </Stack>
                      ) : null}
                    </Stack>
                  </Stack>
                ) : null}
              </Stack>
              <Stack direction="row" justifyContent="flex-end">
                <CustomButton
                  handleOnClick={sendData}
                  text={t('RequestReturn')}
                  sx={{
                    fontFamily: 'JHFontBold',
                    border: '1px solid transparent',
                  }}
                  color="primary"
                  size="small"
                  disabled={buttonDisabled}
                />
              </Stack>
            </Stack>
          </Stack>
        </LocalizationProvider>
      </CardContent>
    </Card>
  );
};
